import i18n, { i18nNS } from '../../i18n';
import { MIN_NO_OF_POLL_QUESTION_OPTIONS } from '../../modules/poll/PollPage/constants';
import { activityTileClasses } from '../../modules/shared/Tile/styles';
import { format, unix } from '../../utils/datetime';
import { omit, pick } from '../../utils/objects';
import { getPreviouslyUsedActivities, getUnpublishedActivities } from '../class-activities';
import { ClassActivityPluralTypes } from '../class-activities/types';
import request from '../request';
import { getIsIntroductionSkipped, setSkipIntroduction } from '../shared/preferences';
import { DialogTypes } from '../shared/types';
import { ClassActivityTypes } from '../types';
import { PollsAPI } from './api-types';
import { SAFE_EMPTY_STRING } from './constants';
import { PreviouslyUsedPollItem, UnpublishedPollItem } from './types';
import urls from './urls';

export async function getUnpublishedPolls(
  payload: PollsAPI.GetUnpublishedPollsRequest
): Promise<PollsAPI.GetUnpublishedPollsTransformedResponse> {
  const { activities }: PollsAPI.GetUnpublishedPollsResponse = await getUnpublishedActivities({
    ...payload,
    activityType: ClassActivityPluralTypes.POLLS,
  });

  const transformedPolls: UnpublishedPollItem[] = activities.map((activity) => ({
    ...omit(activity, 'isAddedToPPT', 'published'),
    isAddedToPPT: activity.isAddedToPPT === 1,
    isPublished: activity.published === 1,
  }));

  return { unpublishedPolls: transformedPolls };
}

export async function getPreviouslyUsedPolls(): Promise<PollsAPI.GetPreviouslyUsedPollsTransformedResponse> {
  const { courseData }: PollsAPI.GetPreviouslyUsedPollsResponse = await getPreviouslyUsedActivities({
    activityType: ClassActivityPluralTypes.POLLS,
  });

  const transformedcourseData: PreviouslyUsedPollItem[] = courseData.map((course) => {
    const { activities, ...courseData } = course;
    return {
      ...courseData,
      activities: activities.map((activity) => {
        const { classData, activityData } = activity;
        return {
          classData,
          activityData: {
            ...activityData,
            isAddedToPPT: activityData.isAddedToPPT === 1,
            isPublished: activityData.published === 1,
          },
        };
      }),
    };
  });

  return { previouslyUsedPolls: transformedcourseData };
}

export async function getPollDetails(
  payload: PollsAPI.GetPollDetailsRequest
): Promise<PollsAPI.GetPollDetailsResponse> {
  const transformedPayload: PollsAPI.GetPollDetailsTransformedRequest = {
    ...payload,
    /** firstAccess key is useful for students only and student will never be allowed to login to ppt */
    firstAccess: 0,
  };

  const { data } = await request.get<PollsAPI.GetPollDetailsResponse>(
    urls.getPollDetails(transformedPayload)
  );

  return data;

  /** pollFactory can be used to transform poll details */
}

export async function createPoll(payload: PollsAPI.CreatePollRequest): Promise<PollsAPI.CreatePollResponse> {
  const { data } = await request.post<PollsAPI.CreatePollResponse>(urls.createPoll, payload);
  return data;
}

export async function createPollQuestion(
  payload: PollsAPI.CreatePollQuestionRequest
): Promise<PollsAPI.CreatePollQuestionResponse> {
  const transformedPayload: PollsAPI.CreatePollQuestionTransformedRequest = {
    ...pick(payload, 'classId'),
    activityId: payload.pollId,
    activityType: ClassActivityPluralTypes.POLLS,
    description: {
      text: SAFE_EMPTY_STRING,
      attachments: [],
    },
    options: new Array(MIN_NO_OF_POLL_QUESTION_OPTIONS).fill(0).map(() => ({
      text: SAFE_EMPTY_STRING,
      type: 'text',
    })),
  };

  const { data } = await request.post<PollsAPI.CreatePollQuestionResponse>(
    urls.createPollQuestion,
    transformedPayload
  );
  return data;
}

export async function updatePoll(payload: PollsAPI.UpdatePollRequest): Promise<PollsAPI.UpdatePollResponse> {
  const transformedPayload: PollsAPI.UpdatePollTransformedRequest = {
    ...omit(payload, 'title', 'isAnonymous'),
    title: payload.title || SAFE_EMPTY_STRING,
    isAnon: payload.isAnonymous ? 1 : 0,
  };
  await request.post<PollsAPI.UpdatePollResponse>(urls.updatePoll, transformedPayload);
}

export async function updatePollQuestion(
  payload: PollsAPI.UpdatePollQuestionRequest
): Promise<PollsAPI.UpdatePollQuestionTransformedResponse> {
  const transformedPayload: PollsAPI.UpdatePollQuestionTransformedRequest = {
    ...pick(payload, 'classId', 'questionId'),
    activityId: payload.pollId,
    activityType: ClassActivityPluralTypes.POLLS,
    description: {
      text: payload.description || SAFE_EMPTY_STRING,
      attachments: payload.attachments,
    },
    options: payload.options.map((option) => ({
      text: option.text || SAFE_EMPTY_STRING,
      type: option.type,
    })),
  };

  await request.post<PollsAPI.UpdatePollQuestionResponse>(urls.updatePollQuestion, transformedPayload);
}

export async function deletePoll(payload: PollsAPI.DeletePollRequest): Promise<PollsAPI.DeletePollResponse> {
  await request.delete<PollsAPI.DeletePollResponse>(urls.deletePoll, { data: payload });
}

export async function savePublishPreferences(
  payload: PollsAPI.SavePublishPreferencesRequest
): Promise<PollsAPI.SavePublishPreferencesResponse> {
  const { allowLate, deadlineFirst, dueDateType, duration, isAnonymous, subscribeToComments } =
    payload.publishPreferences;

  const transformedPayload: PollsAPI.SavePublishPreferencesTransformedRequest = {
    ...pick(payload, 'classId'),
    activityId: payload.pollId,
    activityType: ClassActivityTypes.POLL,
    saveAsDefault: payload.saveAsDefault ? 1 : 0,
    pollPref: {
      allowLate: allowLate ? 1 : 0,
      deadlineFirst: deadlineFirst ? 1 : 0,
      dueDateType,
      duration,
      isAnon: isAnonymous ? 1 : 0,
      subscribeToComments: subscribeToComments ? 1 : 0,
    },
  };

  const { data } = await request.post<PollsAPI.SavePublishPreferencesResponse>(
    urls.savePublishPreferences,
    transformedPayload
  );

  return data;
}

export async function publishPoll(
  payload: PollsAPI.PublishPollRequest
): Promise<PollsAPI.PublishPollResponse> {
  const { classId, pollId, publishPreferences, saveAsDefault } = payload;

  const transformedPayload: PollsAPI.PublishPollTransformedRequest = {
    classId,
    pollId,
    saveAsDefault: saveAsDefault ? 1 : 0,
    toBeDone: publishPreferences.toBeDone,
    allowLate: publishPreferences.allowLate ? 1 : 0,
    deadlineFirst: publishPreferences.deadlineFirst ? 1 : 0,
    dueDateTime: format(unix(), "yyyyMMdd'T'HH:mm"),
    dueDateType: publishPreferences.dueDateType,
    duration: publishPreferences.duration,
    isAnon: publishPreferences.isAnonymous ? 1 : 0,
    subscribeToComments: publishPreferences.subscribeToComments ? 1 : 0,
  };

  const { data } = await request.post<PollsAPI.PublishPollResponse>(urls.publishPoll, transformedPayload);

  return data;
}

export async function stopPoll(payload: PollsAPI.StopPollRequest): Promise<PollsAPI.StopPollResponse> {
  const transformedPayload: PollsAPI.StopPollTransformedRequest = {
    ...pick(payload, 'classId'),
    activityId: payload.pollId,
    activityType: ClassActivityPluralTypes.POLLS,
    localTime: format(unix(), "yyyyMMdd'T'HH:mm"),
  };

  const { data } = await request.post<PollsAPI.StopPollResponse>(urls.stopPoll, transformedPayload);

  return data;
}

export async function setSlideType(
  payload: PollsAPI.SetSlideTypeRequest
): Promise<PollsAPI.SetSlideTypeResponse> {
  const transformedPayload: PollsAPI.SetSlideTypeTransformedRequest = {
    ...payload,
    type: ClassActivityPluralTypes.POLLS,
  };

  await request.put<PollsAPI.SetSlideTypeResponse>(urls.setSlideType, transformedPayload);
}

export async function unsetSlideType(
  payload: PollsAPI.UnsetSlideTypeRequest
): Promise<PollsAPI.UnsetSlideTypeResponse> {
  const transformedPayload: PollsAPI.UnsetSlideTypeTransformedRequest = {
    ...payload,
    type: ClassActivityPluralTypes.POLLS,
  };

  await request.put<PollsAPI.UnsetSlideTypeResponse>(urls.unsetSlideType, transformedPayload);
}

export function setBarGraphSkipIntroduction(value: boolean) {
  setSkipIntroduction(DialogTypes.BAR_GRAPH, value);
}

export function getIsBarGraphIntroductionSkipped() {
  return getIsIntroductionSkipped(DialogTypes.BAR_GRAPH);
}

export function setPieChartSkipIntroduction(value: boolean) {
  setSkipIntroduction(DialogTypes.PIE_CHART, value);
}

export function getIsPieChartIntroductionSkipped() {
  return getIsIntroductionSkipped(DialogTypes.PIE_CHART);
}

export function setCopyActivitySkipIntroduction(value: boolean) {
  setSkipIntroduction(DialogTypes.COPY_ACTIVITY, value);
}

export function getIsCopyActivityIntroductionSkipped() {
  return getIsIntroductionSkipped(DialogTypes.COPY_ACTIVITY);
}
